/**
 * What a widget costs, in words — the one place a page turns a widget's prices into
 * a phrase (session 46c, `.docs/46c-widget-monthly-fee.md` rule 11).
 *
 * A widget has two prices since session 46c, a unit price and a monthly fee, and three
 * shapes follow: **metered** ("$0.12 per conversation, 1,000 included"), **flat**
 * ("$5.00 a month" — the blog) and **free** ("Free" — the forms), and a metered widget
 * may carry a fee as well ("$10.00 a month, plus $0.25 per session, 100 included").
 * Every surface that states a widget's price — the widget's page, the billing page's
 * widget table, the publish panel, the caps panel — asks this, so the shapes are
 * worded once and a page decides nothing about which one it has.
 *
 * Which shape is `pricing.isMetered`'s answer, the predicate the API asks too; every
 * figure goes through the formatter (`useFormat()`), and nothing is computed.
 */

import { isMetered, isZero, type Money, type WidgetView } from '@fareast/shared'

import type { Formatter } from './useFormat.js'

/** The four fields a price is made of. A `WidgetView` has them all. */
export type WidgetPrice = Pick<
  WidgetView,
  'unit_amount' | 'monthly_amount' | 'unit_noun' | 'included_qty'
>

export type WidgetPriceWordsOptions = {
  /**
   * False for a page that states the allowance on a line of its own — the widget
   * page's "Included / mo" row — so it is not said twice. Default true.
   */
  readonly included?: boolean
}

/** "$5.00 a month". The fee half of every phrase below, and the fee on its own. */
export function monthlyFeeWords(fee: Money, format: Pick<Formatter, 'money'>): string {
  return `${format.money(fee)} a month`
}

/**
 * "$0.12 per conversation, 1,000 included", "$5.00 a month", "Free", or both prices of
 * a metered widget with a fee — the fee first, because it is the part that is billed
 * whatever happens. An allowance of nothing is left out rather than said: a prepay
 * widget includes none, and "0 included" reads as a restriction.
 */
export function widgetPriceWords(
  widget: WidgetPrice,
  format: Pick<Formatter, 'money' | 'number'>,
  options: WidgetPriceWordsOptions = {},
): string {
  const fee = isZero(widget.monthly_amount) ? null : monthlyFeeWords(widget.monthly_amount, format)
  if (!isMetered(widget.unit_amount.amount)) return fee ?? 'Free'

  const allowance =
    (options.included ?? true) && widget.included_qty > 0
      ? `, ${format.number(widget.included_qty)} included`
      : ''
  const perUnit = `${format.money(widget.unit_amount)} per ${widget.unit_noun}${allowance}`
  return fee === null ? perUnit : `${fee}, plus ${perUnit}`
}
